/**
 * 产品目录 —— 大菜单、产品页与站内搜索共用的唯一事实来源。
 *
 * 每个条目的能力描述都能在 `platform/` 里找到出处（模块、阈值、权限、接入协议、部署形态），
 * 不新增事实、不估算数字。第八类「即将上线」的四款产品信息由需求方提供，
 * 它们**没有价格、没有购买入口**，动作只有「了解规划」。
 */
import type { IconName } from './icons';
import type { ActionLink } from './home';

export interface ProductCategory {
  id: string;
  name: string;
  /** 大菜单左侧的类目说明，一句话。 */
  summary: string;
  icon: IconName;
}

export interface ProductEntry {
  id: string;
  categoryId: string;
  name: string;
  /** 卡片上的一行描述。 */
  tagline: string;
  icon: IconName;
  badge?: 'hot' | 'new' | 'soon';
  status: 'live' | 'soon';
  /** 是否进入「精选产品」类目。阿里云的产品页同样把部分条目重复呈现一次。 */
  featured?: boolean;
  /** 完整的说明，仅产品页展开时使用。 */
  note?: string;
  actions: readonly ActionLink[];
}

export const BADGE_LABELS: Record<NonNullable<ProductEntry['badge']>, string> = {
  hot: '热门',
  new: '新增',
  soon: '即将上线',
};

/** 精选产品不单独维护条目，而是从其余类目里挑。 */
export const FEATURED_CATEGORY_ID = 'featured';
export const COMING_SOON_CATEGORY_ID = 'coming-soon';

/** 大菜单与产品页的默认类目。单独拎出来，省掉调用方的空值判断。 */
export const FEATURED_CATEGORY: ProductCategory = {
  id: FEATURED_CATEGORY_ID,
  name: '精选产品',
  summary: '使用频率最高的六项能力，可先从这里看起。',
  icon: 'zap',
};

export const PRODUCT_CATEGORIES: readonly ProductCategory[] = [
  FEATURED_CATEGORY,
  {
    id: 'platform',
    name: '平台与监管',
    summary: '从建档到追溯的完整链路，监管场景的主要工作台。',
    icon: 'fleet',
  },
  {
    id: 'realtime',
    name: '实时遥测',
    summary: '浏览器直连机体的数据链路，以及链路劣化时的降级处理。',
    icon: 'link',
  },
  {
    id: 'alerting',
    name: '告警与阈值',
    summary: '三级阈值与三层覆盖，决定何时提醒、提醒到什么程度。',
    icon: 'bell',
  },
  {
    id: 'iam',
    name: '权限与审计',
    summary: '谁能动、动过什么，以及凭据如何下发与吊销。',
    icon: 'shield',
  },
  {
    id: 'access',
    name: '开放接入',
    summary: '一份接口约定，配合内置模拟机队即可完成联调。',
    icon: 'plug',
  },
  {
    id: 'deploy',
    name: '部署与交付',
    summary: '托管于边缘、部署到贵方自有账号，或按场景定制。',
    icon: 'layers',
  },
  {
    id: COMING_SOON_CATEGORY_ID,
    name: '即将上线',
    summary: '正在规划中的产品，尚无发布时间与报价。',
    icon: 'clock',
  },
];

export const PRODUCTS: readonly ProductEntry[] = [
  // —— 平台与监管 ——
  {
    id: 'fleet-overview',
    categoryId: 'platform',
    name: '机队总览',
    tagline: '全部机体的状态汇聚于一屏，异常的机体自动进入您的视野。',
    note: '地图上呈现位置与航迹，卡片上呈现电量进度与链路质量分级；由卡片可直接进入单机管理。',
    icon: 'fleet',
    badge: 'hot',
    status: 'live',
    featured: true,
    actions: [{ label: '查看模块说明', to: '/platform#modules', variant: 'secondary' }],
  },
  {
    id: 'device-registry',
    categoryId: 'platform',
    name: '无人机管理',
    tagline: '一份设备档案，决定后续所有链路能否建立。',
    note: '支持整支机队批量导入，每台机体持有独立的接入凭据，端点字段与机型适配配置都在这里维护。',
    icon: 'registry',
    status: 'live',
    actions: [{ label: '查看模块说明', to: '/platform#modules', variant: 'secondary' }],
  },
  {
    id: 'single-drone',
    categoryId: 'platform',
    name: '单机管理',
    tagline: '单台机体的全部信息与全部操作，集中于同一页面。',
    note: '地图、姿态球与大数字指标并列，另附完整遥测列表；指令下发之前先经过条件检查清单。',
    icon: 'tracking',
    badge: 'hot',
    status: 'live',
    featured: true,
    actions: [{ label: '查看模块说明', to: '/platform#modules', variant: 'secondary' }],
  },
  {
    id: 'user-management',
    categoryId: 'platform',
    name: '用户管理',
    tagline: '权限并非写在备注里，而是前后端共用的一份定义。',
    note: '四种内置角色覆盖常见分工；界面收敛入口，Worker 强制执行鉴权。',
    icon: 'users',
    status: 'live',
    featured: true,
    actions: [{ label: '查看模块说明', to: '/platform#modules', variant: 'secondary' }],
  },
  {
    id: 'platform-audit-log',
    categoryId: 'platform',
    name: '审计日志',
    tagline: '发生事故时能够查证责任归属，这是监管场景的底线。',
    note: '敏感操作逐条留档，可按时间与操作人检索，默认保留 180 天。',
    icon: 'audit',
    badge: 'hot',
    status: 'live',
    featured: true,
    actions: [{ label: '查看模块说明', to: '/platform#modules', variant: 'secondary' }],
  },
  {
    id: 'platform-settings',
    categoryId: 'platform',
    name: '系统设置',
    tagline: '平台级参数在此统一调整，改动即时生效并留档。',
    note: '告警阈值、凭据轮换周期与断链判定时长都收敛在这一处。',
    icon: 'settings',
    status: 'live',
    actions: [{ label: '查看模块说明', to: '/platform#modules', variant: 'secondary' }],
  },

  // —— 实时遥测 ——
  {
    id: 'telemetry-stream',
    categoryId: 'realtime',
    name: '遥测推流',
    tagline: '浏览器与机体直接建立连接，遥测不经过平台中转。',
    note: '机体按 1 Hz 上报 MAVLink 数据。平台不转发、不存储，因此机队规模扩大时不会产生额外的带宽开销。',
    icon: 'link',
    badge: 'hot',
    status: 'live',
    featured: true,
    actions: [{ label: '查看链路设计', to: '/platform#realtime', variant: 'secondary' }],
  },
  {
    id: 'polling-fallback',
    categoryId: 'realtime',
    name: '轮询降级',
    tagline: '推流中断后按 1、2、4 秒退避重连，同时改用轮询取值。',
    note: '界面持续更新，仅频率与实时性下降。链路降级可以接受，掩盖降级不可接受。',
    icon: 'refresh',
    status: 'live',
    actions: [{ label: '查看链路设计', to: '/platform#realtime', variant: 'secondary' }],
  },
  {
    id: 'link-timeout',
    categoryId: 'realtime',
    name: '断链判定',
    tagline: '超过设定时长未收到新数据即判定失联，不以历史数据继续渲染。',
    note: '默认 3000 ms，可在 500–60000 ms 之间配置；判定时会说明原因是超时，而非其他因素。',
    icon: 'clock',
    status: 'live',
    actions: [{ label: '查看链路设计', to: '/platform#realtime', variant: 'secondary' }],
  },
  {
    id: 'map-and-track',
    categoryId: 'realtime',
    name: '地图与航迹',
    tagline: '机体位置与本次浏览累积的航迹，刷新即清空。',
    note: '平台不保存历史航迹。您看到的是本次会话的数据，离开页面即不复存在。',
    icon: 'route',
    status: 'live',
    actions: [{ label: '查看链路设计', to: '/platform#realtime', variant: 'secondary' }],
  },
  {
    id: 'attitude-and-gauges',
    categoryId: 'realtime',
    name: '姿态与仪表',
    tagline: '姿态球与大数字指标并列，1 Hz 数据下不做无意义的跳变。',
    note: '数值区域使用等宽数字，避免读数在刷新时左右抖动。',
    icon: 'gauge',
    status: 'live',
    actions: [{ label: '查看链路设计', to: '/platform#realtime', variant: 'secondary' }],
  },
  {
    id: 'link-quality',
    categoryId: 'realtime',
    name: '链路质量分级',
    tagline: '良好、一般、稍弱、很差四档，附死区与驻留判定。',
    note: '只呈现分级而不呈现原始数值，避免 1 Hz 数据下末位数字持续跳动带来的误导。',
    icon: 'signal',
    status: 'live',
    actions: [{ label: '查看链路设计', to: '/platform#realtime', variant: 'secondary' }],
  },

  // —— 告警与阈值 ——
  {
    id: 'three-tier-thresholds',
    categoryId: 'alerting',
    name: '三级阈值',
    tagline: '注意、警告、严重三级，覆盖电量、链路质量与延迟。',
    note: '每级都有明确的处理要求：注意预留反应时间，警告即刻处理，严重按立即处置对待。',
    icon: 'bell',
    badge: 'hot',
    status: 'live',
    actions: [{ label: '查看阈值设计', to: '/platform#thresholds', variant: 'secondary' }],
  },
  {
    id: 'threshold-override',
    categoryId: 'alerting',
    name: '阈值覆盖',
    tagline: '平台默认、平台设置、单机三层覆盖，改动逐层生效。',
    note: '现场网络普遍较差时可整体放宽延迟要求；某台机体工况特殊时可单独设置，不影响其余成员。',
    icon: 'sliders',
    status: 'live',
    actions: [{ label: '查看阈值设计', to: '/platform#thresholds', variant: 'secondary' }],
  },
  {
    id: 'alert-panel',
    categoryId: 'alerting',
    name: '告警面板',
    tagline: '链路降级、丢帧与延迟汇聚于同一面板，按级标色。',
    note: '面板与单机页共用同一份阈值定义，因此不会出现两处结论不一致的情况。',
    icon: 'alert',
    status: 'live',
    actions: [{ label: '查看阈值设计', to: '/platform#thresholds', variant: 'secondary' }],
  },

  // —— 权限与审计 ——
  {
    id: 'role-permissions',
    categoryId: 'iam',
    name: '角色权限',
    tagline: '管理员、操作员、维护员、观察员四类内置角色。',
    note: '操作员可下发起降与航线指令，维护员只管设备档案与凭据轮换，观察员只能看。',
    icon: 'users',
    status: 'live',
    actions: [{ label: '查看角色定义', to: '/platform#permissions', variant: 'secondary' }],
  },
  {
    id: 'three-layer-defense',
    categoryId: 'iam',
    name: '三层防线',
    tagline: '导航与路由、界面操作、Worker 强制鉴权。',
    note: '前两层关乎使用体验，第三层才是真正的安全边界 —— 每个受保护的接口都经过同一层鉴权。',
    icon: 'shield',
    badge: 'hot',
    status: 'live',
    featured: true,
    actions: [{ label: '查看角色定义', to: '/platform#permissions', variant: 'secondary' }],
  },
  {
    id: 'credential-modes',
    categoryId: 'iam',
    name: '凭据模式',
    tagline: '长期 API Key、短期平台令牌，或不向机体下发凭据。',
    note: '按机体实际所处的网络环境选择。短期令牌由平台签发，可随时吊销。',
    icon: 'key',
    status: 'live',
    actions: [{ label: '查看接入协议', to: '/platform#access', variant: 'secondary' }],
  },
  {
    id: 'login-security',
    categoryId: 'iam',
    name: '登录安全',
    tagline: '首位管理员首次登录须修改初始密码。',
    note: '账号与会话状态由 Worker 统一校验，不由前端自行判断。',
    icon: 'lock',
    status: 'live',
    actions: [{ label: '查看角色定义', to: '/platform#permissions', variant: 'secondary' }],
  },

  // —— 开放接入 ——
  {
    id: 'drone-api',
    categoryId: 'access',
    name: '接入协议',
    tagline: '一份 REST 接口约定，任何满足约定的机体均可接入。',
    note: '平台仅依据 HTTP 状态码分支判断：先探活，再建流，错误按状态码分类处理。',
    icon: 'plug',
    badge: 'hot',
    status: 'live',
    featured: true,
    actions: [{ label: '查看接入协议', to: '/platform#access', variant: 'secondary' }],
  },
  {
    id: 'endpoint-config',
    categoryId: 'access',
    name: '端点配置',
    tagline: '协议、域名、端口、REST 根路径与推流路径，逐项填写。',
    note: '端点字段随设备档案一同维护，改绑定不影响其他机体。',
    icon: 'globe',
    status: 'live',
    actions: [{ label: '查看接入协议', to: '/platform#access', variant: 'secondary' }],
  },
  {
    id: 'status-code-contract',
    categoryId: 'access',
    name: '错误码约定',
    tagline: '五类状态码对应五种处理方式，由机体侧保证准确。',
    note: '错误体中的 message 会原样呈现给操作员，因此接入方应确保状态码准确，而非让文案承担判断职责。',
    icon: 'info',
    status: 'live',
    actions: [{ label: '查看接入协议', to: '/platform#access', variant: 'secondary' }],
  },
  {
    id: 'mock-fleet',
    categoryId: 'access',
    name: '模拟机队',
    tagline: '内置 6 台模拟机体，尚未配备真机也能走通全流程。',
    note: '各占一个端口，并可主动注入断链与慢响应，便于验证降级逻辑。',
    icon: 'cpu',
    badge: 'new',
    status: 'live',
    actions: [{ label: '申请试用', to: '/pricing#trial', variant: 'secondary' }],
  },
  {
    id: 'developer-access',
    categoryId: 'access',
    name: '个人开发者接入',
    tagline: '单人单机同样可以接入，注册后即可开始。',
    note: '面向自研机体与 DIY 场景，不要求先有整支机队。',
    icon: 'code',
    status: 'live',
    actions: [{ label: '查看适用场景', to: '/solutions#diy', variant: 'secondary' }],
  },

  // —— 部署与交付 ——
  {
    id: 'cloud-hosting',
    categoryId: 'deploy',
    name: '云端托管',
    tagline: '控制面运行于边缘节点，按请求计费，空闲成本接近于零。',
    note: '接口、鉴权与审计运行于 Worker，结构化数据保存于 D1，前端为静态站点。',
    icon: 'globe',
    status: 'live',
    actions: [{ label: '查看部署方式', to: '/platform#deploy', variant: 'secondary' }],
  },
  {
    id: 'private-deployment',
    categoryId: 'deploy',
    name: '私有化部署',
    tagline: '部署到贵方自有账号与域名，数据与账号体系由贵方掌握。',
    note: '建表脚本与种子数据随代码库一并交付，升级路径可事先约定。',
    icon: 'layers',
    status: 'live',
    actions: [{ label: '查看部署方式', to: '/platform#deploy', variant: 'secondary' }],
  },
  {
    id: 'enterprise-customization',
    categoryId: 'deploy',
    name: '企业定制',
    tagline: '按场景补充字段、流程与界面，而非让业务迁就软件。',
    note: '定制范围与验收标准在方案阶段逐条确定。',
    icon: 'building',
    status: 'live',
    actions: [{ label: '咨询定制方案', to: '/contact', variant: 'secondary' }],
  },

  // —— 即将上线（规划中，无价格、无购买入口）——
  {
    id: 'iot-platform',
    categoryId: COMING_SOON_CATEGORY_ID,
    name: '物联网平台',
    tagline: '偏向设备侧的接入与客户管理。',
    note: '面向设备接入与客户／租户管理。与监管平台的分工仍在定义中。',
    icon: 'globe',
    badge: 'soon',
    status: 'soon',
    actions: [{ label: '了解规划', to: '/contact', variant: 'secondary' }],
  },
  {
    id: 'wind-forecast',
    categoryId: COMING_SOON_CATEGORY_ID,
    name: '风场预测平台',
    tagline: '面向航线与作业窗口规划，提供风切变与阵风的短期预报。',
    note: '用于判断某个时段是否适合起飞与作业。空间尺度、时间尺度与数据来源仍在定义中。',
    icon: 'gauge',
    badge: 'soon',
    status: 'soon',
    actions: [{ label: '了解规划', to: '/contact', variant: 'secondary' }],
  },
  {
    id: 'network-rtk',
    categoryId: COMING_SOON_CATEGORY_ID,
    name: '无人机网络 RTK',
    tagline: '面向机队提供网络 RTK 差分服务，把定位精度提升到厘米级。',
    note: '高精度定位会并入监管平台，用于航迹与作业精度保障。我们不自行建设基准站网络，而是以代理方式接入现有服务。',
    icon: 'pin',
    badge: 'soon',
    status: 'soon',
    actions: [{ label: '了解规划', to: '/contact', variant: 'secondary' }],
  },
  {
    id: 'health-module',
    categoryId: COMING_SOON_CATEGORY_ID,
    name: '4G 无人机硬件健康管理板',
    tagline: '面向飞控底层的一枚机载管理模块。',
    note: '相当于无人机上的 BIOS 或服务器上的 iDRAC：自带独立 UPS 电池、GPS 与 IMU，接收飞控心跳包，可在掉电失联后作为找机模块，也可远程唤醒机体执行任务。它管理的是飞控本身执行不到的底层。',
    icon: 'cpu',
    badge: 'soon',
    status: 'soon',
    actions: [{ label: '了解规划', to: '/contact', variant: 'secondary' }],
  },
];

export const COMING_SOON: readonly ProductEntry[] = PRODUCTS.filter(
  (product) => product.categoryId === COMING_SOON_CATEGORY_ID,
);

export const PRODUCTS_INTRO = {
  eyebrow: '产品目录',
  title: '完整的产品体系，为低空运营搭好底座',
  lead: '监管平台是主体，围绕它还有实时遥测、告警阈值、权限审计、开放接入与部署交付。四款正在规划中的产品单独列在最后，尚无发布时间与报价。',
  actions: [
    { label: '进入平台', to: 'https://havorix.work', external: true, variant: 'primary' },
    { label: '申请试用', to: '/pricing#trial', variant: 'secondary' },
  ],
} as const;

/** 按类目取产品。「精选产品」返回带 featured 标记的那些，其余按 categoryId 取。 */
export function productsInCategory(categoryId: string): readonly ProductEntry[] {
  if (categoryId === FEATURED_CATEGORY_ID) {
    return PRODUCTS.filter((product) => product.featured);
  }
  return PRODUCTS.filter((product) => product.categoryId === categoryId);
}
